import React, { useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudArrowUp, faImage, faTimes, faSpinner } from '@fortawesome/free-solid-svg-icons';
import './ImageUpload.css';

const ImageUpload = ({ value, onChange, onUpload }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(value || '');
  const fileInputRef = useRef(null);

  // Validate if URL is a complete image URL
  const isValidImageUrl = (url) => {
    if (!url) return false;
    // Check if URL ends with / (directory) or has no file extension
    if (url.endsWith('/')) return false;
    // Check if URL has a valid image extension or is a data URL
    const imageExtensions = /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i;
    const isDataUrl = url.startsWith('data:image/');
    return isDataUrl || imageExtensions.test(url);
  };

  // Update preview when value prop changes, but only if it's a valid image URL
  React.useEffect(() => {
    if (isValidImageUrl(value)) {
      setPreview(value);
    } else if (value && !isValidImageUrl(value)) {
      // Clear preview if value is invalid
      setPreview('');
    }
  }, [value]);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFile(files[0]);
    }
  };

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files && files[0]) {
      handleFile(files[0]);
    }
  };

  const handleFile = async (file) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    // Upload file if onUpload is provided
    if (onUpload) {
      setUploading(true);
      
      // Show local preview while uploading
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);

      try {
        const uploadedUrl = await onUpload(file);
        // Force preview update with server URL
        setPreview(''); // Clear first
        setTimeout(() => {
          setPreview(uploadedUrl);
          onChange(uploadedUrl);
        }, 100);
      } catch (error) {
        console.error('Upload error:', error);
        alert('Failed to upload image: ' + (error.response?.data?.message || error.message));
        setPreview('');
      } finally {
        setUploading(false);
      }
    }
  };

  const handleRemove = () => {
    setPreview('');
    onChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="image-upload-container">
      {preview ? (
        <div className="image-upload-preview">
          <img 
            src={preview} 
            alt="Preview"
            onError={(e) => {
              console.error('Image load error:', preview);
              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200"%3E%3Crect fill="%23f1f5f9" width="200" height="200"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" fill="%2364748b" font-size="14"%3EImage Error%3C/text%3E%3C/svg%3E';
            }}
          />
          <div className="image-upload-overlay">
            <button
              type="button"
              onClick={handleRemove}
              className="image-upload-remove"
              disabled={uploading}
            >
              <FontAwesomeIcon icon={faTimes} />
              Remove
            </button>
          </div>
          {uploading && (
            <div className="image-upload-loading">
              <FontAwesomeIcon icon={faSpinner} spin />
              <span>Uploading...</span>
            </div>
          )}
        </div>
      ) : (
        <div
          className={`image-upload-dropzone ${isDragging ? 'dragging' : ''}`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={handleClick}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          
          <div className="image-upload-icon">
            <FontAwesomeIcon icon={isDragging ? faCloudArrowUp : faImage} />
          </div>
          
          <div className="image-upload-text">
            <p className="image-upload-title">
              {isDragging ? 'Drop image here' : 'Click to upload or drag and drop'}
            </p>
            <p className="image-upload-subtitle">
              PNG, JPG, GIF up to 5MB
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
